import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Grid,
  Card,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Select,
  MenuItem,
  Input,
  InputLabel,
  IconButton,
  FormHelperText,
  InputAdornment,
  Snackbar,
  Alert,
  Chip
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import { API_ENDPOINTS } from "../../../config/api";

const CreateAppUserForm = () => {
  const [userTypes, setUserTypes] = useState([]);
  const [regions, setRegions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [formData, setFormData] = useState({
    user_type_id: "",
    region_id: [],
    company_name: "",
    contact_person: "",
    profile_pic: null,
    mobile: "",
    alternate_number: "",
    email: "",
    street_line: "",
    town_city: "",
    state: "",
    country: "India",
    pincode: "",
    gstin: "",
    pan: "",
  });
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserTypes = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.USER_TYPE_NAMES);
        const userTypesData = Array.isArray(response.data)
          ? response.data
          : response.data.data || Object.values(response.data);
        setUserTypes(userTypesData);
      } catch (error) {
        console.error("Error fetching user types:", error);
        setUserTypes([]);
        setSnackbar({
          open: true,
          message: "Failed to load user types.",
          severity: "error",
        });
      }
    };
    fetchUserTypes();
  }, []);

  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.REGION_NAMES);
        const regionsData = Array.isArray(response.data)
          ? response.data
          : response.data.data || Object.values(response.data);
        setRegions(regionsData);
      } catch (error) {
        console.error("Error fetching regions:", error);
        setRegions([]);
        setSnackbar({
          open: true,
          message: "Failed to load regions.",
          severity: "error",
        });
      }
    };
    fetchRegions();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    let processedValue = value;
    if (name === "mobile" || name === "alternate_number") {
      processedValue = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === "gstin") {
      processedValue = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 15).toUpperCase();
    } else if (name === "pan") {
      processedValue = value.replace(/[^A-Za-z0-9]/g, '').slice(0, 10).toUpperCase();
    } else if (name === "pincode") {
      processedValue = value.replace(/\D/g, '').slice(0, 6);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        setFormData((prev) => ({ ...prev, profile_pic: null }));
        setPreview(null);
        setFileInputKey(Date.now());
        return;
      }
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        setFormData((prev) => ({ ...prev, profile_pic: null }));
        setPreview(null);
        setFileInputKey(Date.now());
        return;
      }
      setError("");
      setFormData((prev) => ({ ...prev, profile_pic: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({ ...prev, profile_pic: null }));
    setPreview(null);
    setError("");
    setFileInputKey(Date.now());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneRegex = /^\d{10}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const gstinRegex = /^[A-Za-z0-9]{15}$/;
    const panRegex = /^[A-Za-z0-9]{10}$/;

    if (!formData.user_type_id) {
      setSnackbar({ open: true, message: "User Type is required", severity: "error" });
      return;
    }
    if (formData.region_id.length === 0) {
      setSnackbar({ open: true, message: "Region is required", severity: "error" });
      return;
    }
    if (!formData.company_name.trim()) {
      setSnackbar({ open: true, message: "Company Name is required", severity: "error" });
      return;
    }
    if (!formData.contact_person.trim()) {
      setSnackbar({ open: true, message: "Contact Person is required", severity: "error" });
      return;
    }
    if (!formData.mobile.trim()) {
      setSnackbar({ open: true, message: "Mobile is required", severity: "error" });
      return;
    }
    if (!phoneRegex.test(formData.mobile)) {
      setSnackbar({ open: true, message: "Mobile number must be 10 digits", severity: "error" });
      return;
    }
    if (formData.alternate_number && !phoneRegex.test(formData.alternate_number)) {
      setSnackbar({ open: true, message: "Alternate number must be 10 digits", severity: "error" });
      return;
    }
    if (!formData.email.trim()) {
      setSnackbar({ open: true, message: "Email is required", severity: "error" });
      return;
    }
    if (!emailRegex.test(formData.email.trim())) {
      setSnackbar({ open: true, message: "Invalid email address", severity: "error" });
      return;
    }
    if (formData.gstin && !gstinRegex.test(formData.gstin)) {
      setSnackbar({ open: true, message: "GSTIN must be 15 alphanumeric characters", severity: "error" });
      return;
    }
    if (formData.pan && !panRegex.test(formData.pan)) {
      setSnackbar({ open: true, message: "PAN must be 10 alphanumeric characters", severity: "error" });
      return;
    }
    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
      setSnackbar({ open: true, message: "Pincode must be exactly 6 digits", severity: "error" });
      return;
    }

    setSubmitting(true);
    const submitData = new FormData();
    submitData.append("user_type_id", formData.user_type_id);
    formData.region_id.forEach(id => {
      submitData.append("region_id[]", id);
    });
    submitData.append("company_name", formData.company_name);
    submitData.append("contact_person", formData.contact_person);
    submitData.append("mobile", formData.mobile);
    submitData.append("alternate_number", formData.alternate_number);
    submitData.append("email", formData.email);
    submitData.append("street_line", formData.street_line);
    submitData.append("town_city", formData.town_city);
    submitData.append("state", formData.state);
    submitData.append("country", formData.country);
    submitData.append("pincode", formData.pincode);
    submitData.append("gstin", formData.gstin);
    submitData.append("pan", formData.pan);
    if (formData.profile_pic instanceof File) {
      submitData.append("profile_pic", formData.profile_pic);
    }

    try {
      await axios.post(API_ENDPOINTS.APP_USERS.CREATE, submitData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSnackbar({
        open: true,
        message: "App User created successfully!",
        severity: "success",
      });
      setTimeout(() => navigate("/app-users"), 1500);
    } catch (error) {
      console.error("Error creating app user:", error);
      let userMessage = error.response?.data?.message || "Failed to create app user.";
      if (userMessage.includes("foreign key constraint fails")) {
        userMessage = "Cannot create app user due to related records. Please check your input.";
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false, message: "", severity: "success" }));
  };

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ p: 3, width: "100%", boxShadow: 3 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12}>
                  <FormControl fullWidth>
                    <div className="image-field">
                      <Input
                        id="profile_pic"
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        key={fileInputKey}
                        sx={{ display: 'none' }}
                      />
                      <label htmlFor="profile_pic">
                        <Box
                          sx={{
                            border: '2px dashed #B0C4DE',
                            borderRadius: '10px',
                            padding: '20px',
                            textAlign: 'center',
                            backgroundColor: '#F0F8FF',
                            cursor: 'pointer',
                            minHeight: '120px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                          }}
                        >
                          {preview ? (
                            <>
                              <img
                                src={preview}
                                alt="Uploaded"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '80px',
                                  marginBottom: '10px',
                                  objectFit: 'contain',
                                }}
                              />
                              <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                                {formData.profile_pic?.name}
                              </Typography>
                              <IconButton
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleRemoveFile();
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: '5px',
                                  right: '5px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <UploadIcon
                                sx={{ fontSize: '40px', color: '#4682B4', marginBottom: '10px' }}
                              />
                              <Typography variant="body1" sx={{ color: '#4682B4', fontWeight: 'bold' }}>
                                Upload Profile Picture
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#4682B4' }}>
                                Supports: JPG, JPEG, PNG
                              </Typography>
                            </>
                          )}
                        </Box>
                      </label>
                    </div>
                    {error && <FormHelperText error>{error}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel id="user-type-label">User Type</InputLabel>
                    <Select
                      labelId="user-type-label"
                      name="user_type_id"
                      value={formData.user_type_id}
                      label="User Type"
                      onChange={handleChange}
                    >
                      <MenuItem value="">Select User Type</MenuItem>
                      {userTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Company Name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Contact Person Name"
                      name="contact_person"
                      value={formData.contact_person}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
              
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Mobile"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      required
                      variant="outlined"
                      inputProps={{ maxLength: 10 }}
                      helperText="Note: Enter 10 digit mobile number"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Alternate Number"
                      name="alternate_number"
                      value={formData.alternate_number}
                      onChange={handleChange}
                      variant="outlined"
                      inputProps={{ maxLength: 10 }}
                      helperText="Note: Enter 10 digit alternate number (optional)"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      type="email"
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                
               
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="City"
                      name="town_city"
                      value={formData.town_city}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Street Line"
                      name="street_line"
                      value={formData.street_line}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Pincode"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      inputProps={{ 
                        maxLength: 6,
                        pattern: "[0-9]*",
                        inputMode: "numeric"
                      }}
                      helperText="Note: Enter 6 digit pincode (optional)"
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="GSTIN"
                      name="gstin"
                      value={formData.gstin}
                      onChange={handleChange}
                      variant="outlined"
                      inputProps={{ maxLength: 15 }}
                      helperText="Note: Enter 15 alphanumeric characters (optional)"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="PAN Number"
                      name="pan"
                      value={formData.pan}
                      onChange={handleChange}
                      variant="outlined"
                      inputProps={{ maxLength: 10 }}
                      helperText="Note: Enter 10 alphanumeric characters (optional)"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', m: 1.5 }}>
                    Region *
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {regions.map((region) => (
                      <Chip
                        key={region.id}
                        label={region.name}
                        onClick={() => {
                          const newSelection = formData.region_id.includes(region.id)
                            ? formData.region_id.filter((id) => id !== region.id)
                            : [...formData.region_id, region.id];
                          handleChange({
                            target: {
                              name: 'region_id',
                              value: newSelection,
                            },
                          });
                        }}
                        color={formData.region_id.includes(region.id) ? 'primary' : 'default'}
                        variant={formData.region_id.includes(region.id) ? 'filled' : 'outlined'}
                        sx={{
                          borderRadius: '8px',
                          '&:hover': {
                            bgcolor: formData.region_id.includes(region.id)
                              ? 'primary.dark'
                              : 'grey.200',
                            cursor: 'pointer',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>
              <Box mt={3} display="flex" justifyContent="center" gap={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitting}
                >
                  {submitting ? "Creating..." : "Create App User"}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  onClick={() => navigate("/app-users")}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateAppUserForm;